
import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';

interface VitalScoreMetricCircleProps {
  score: number;
  label: string;
  icon: React.ReactNode;
  className?: string;
}

const VitalScoreMetricCircle: React.FC<VitalScoreMetricCircleProps> = React.memo(({ 
  score, 
  label, 
  icon, 
  className 
}) => {
  // Memoizar cálculos del círculo
  const circleData = useMemo(() => {
    const safeScore = typeof score === 'number' && !isNaN(score) ? 
      Math.max(0, Math.min(100, score)) : 0;
    
    const percentage = safeScore;
    const circumference = 2 * Math.PI * 15; // Radio: 15
    const dashOffset = circumference - (percentage / 100) * circumference;

    return {
      percentage,
      circumference,
      dashOffset
    };
  }, [score]);

  return (
    <div className={cn(
      "flex flex-col items-center p-2 rounded-lg border", 
      className
    )}>
      <div className="relative w-16 h-16 flex items-center justify-center">
        <svg width="100%" height="100%" viewBox="0 0 40 40">
          {/* Background circle */}
          <circle 
            cx="20" 
            cy="20" 
            r="15" 
            fill="none" 
            strokeWidth="3" 
            stroke="rgba(255,255,255,0.1)" 
          />
          {/* Progress circle */}
          <circle 
            cx="20" 
            cy="20" 
            r="15" 
            fill="none" 
            strokeWidth="3" 
            stroke="currentColor" 
            strokeLinecap="round" 
            strokeDasharray={circleData.circumference} 
            strokeDashoffset={circleData.dashOffset} 
            transform="rotate(-90 20 20)" 
            className="text-purple-500" 
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          {icon}
        </div>
      </div>
      <div className="mt-2 text-center">
        <p className="text-xs font-medium">{label}</p>
        <p className="text-xs font-bold">{circleData.percentage}%</p>
      </div>
    </div>
  );
});

VitalScoreMetricCircle.displayName = 'VitalScoreMetricCircle';

export default VitalScoreMetricCircle;
